import { NextPage, NextPageContext } from 'next'
import dynamic from 'next/dynamic'
import { getIDeviceComponent } from '@ucheba/utils/helpers/components'
import {
  IObjectDevicesComponents,
  IPageProps,
} from '@ucheba/utils/helpers/components/types'

import React from 'react'
import {
  specialitiesIdSelectors,
  specialitiesIdThunks,
} from '@ucheba/store/specialities/id'
import { olympiadsSelectors, olympiadsThunks } from '@ucheba/store/olympiads'
import { institutionsSelectors, institutionsThunks } from '@ucheba/store/institutions'
import { professionsSelectors, professionsThunks } from '@ucheba/store/professions'
import {
  specialitiesIdSimilarSelectors,
  specialitiesIdSimilarThunks,
} from '@ucheba/store/specialities/id/similar'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { NextSeo } from 'next-seo'
import { useSelector } from 'react-redux'
import { Store } from 'redux'
import { plannerSpecialtiesThunks } from '@ucheba/store/planner/specialties'
import { getIsForInstitution } from './bll'
import Custom404 from '../../../404.page'
import { headerActions } from '../../../../components/Header/store'

const devicesPages: IObjectDevicesComponents = {
  desktop: dynamic(() => import('./desktop')),
  touch: dynamic(() => import('./touch')),
}

const SpecialityIdPage: NextPage<IPageProps> = (props) => {
  const { content, notFound } = props

  const similar = useSelector(specialitiesIdSimilarSelectors.selectAll)
  const olympiads = useSelector(olympiadsSelectors.selectAll)
  const institutions = useSelector(institutionsSelectors.selectAll)
  const professions = useSelector(professionsSelectors.selectAll)
  const entity = useSelector(specialitiesIdSelectors.entity)

  const data = {
    similar,
    olympiads,
    institutions,
    professions,
    ...entity,
  }

  if (notFound) {
    return <Custom404 />
  }

  let { description } = content.seo.meta

  if (content.seo.meta.description.length > 300) {
    const newDescription = content.seo.meta.description.slice(0, 297)

    description = `${newDescription}...`
  }

  return (
    <>
      <NextSeo
        title={content.seo.meta.title}
        description={description}
        openGraph={{
          title: content.seo.meta.title,
          description,
        }}
      />

      {getIDeviceComponent({ ...props, content: data }, devicesPages)}
    </>
  )
}

SpecialityIdPage.getInitialProps = async (
  ctx: NextPageContext & { store: Store }
): Promise<IPageProps> => {
  const { query, store, res } = ctx
  const { dispatch } = store
  const id = query?.id

  await dispatch(specialitiesIdThunks.fetch({ ctx, data: { id } }))
  await dispatch(plannerSpecialtiesThunks.getSpecialitiesIds({ ctx }))
  const entity = specialitiesIdSelectors.entity(store.getState())

  if (!entity?.id) {
    if (res) {
      res.statusCode = 404
    }

    return {
      notFound: true,
    }
  }

  const isForInstitution = getIsForInstitution(entity.specialityType.value)
  const programTypes = isForInstitution
    ? [EProgramTypes.higherBachelor, EProgramTypes.higherSpeciality]
    : [EProgramTypes.vocationalNpo, EProgramTypes.vocationalSpo]

  await Promise.all([
    dispatch(
      institutionsThunks.fetch({
        ctx,
        data: { specialityIds: [id], programTypes, limit: 5 },
      })
    ),
    dispatch(
      specialitiesIdSimilarThunks.fetch({
        ctx,
        data: { id, programTypes, limit: 3 },
      })
    ),
    dispatch(olympiadsThunks.fetch({ ctx, data: { specialityId: id } })),
    dispatch(professionsThunks.fetch({ ctx, data: { specialityIds: [id] } })),
  ])

  const title = entity
    ? `Специальность ${entity.name}, ${entity.codeOkso}, профили, экзамены ЕГЭ, проходные баллы и стоимость обучения`
    : ''

  const description = (() => {
    if (entity.shortDescription) {
      return entity.shortDescription
    }

    if (entity.specificity) {
      return entity.specificity
    }

    return `Специальность ${entity.name}, ${entity.codeOkso}, профили, экзамены ЕГЭ, проходные баллы и стоимость обучения - Учеба.ру`
  })()

  dispatch(headerActions.toggleHeader(true))

  return {
    content: {
      seo: {
        meta: {
          title,
          description,
        },
      },
    },
  }
}

export default SpecialityIdPage
